import React, {useContext, useEffect, useState} from "react";
import {message, Select} from "antd";
import {AiReportSummarizerServiceContext, AppContextContext} from "../../Contexts";
import AiReportSummarizer from "../../domain/AiReportSummarizer";
import {Permission} from "../../domain/auth/Permission";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import QueryOptions from "../../sal-ui/QueryOptions";
import {orderSystemAndOrganizationOptionGroups} from "../../utils/SelectOptionUtils";

const aiReportSummarizerReadPermissions = [
    Permission.AI_REPORT_SUMMARIZER__READ_SEAL,
    Permission.AI_REPORT_SUMMARIZER__READ_ORG,
    Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG
];

interface Props {
    organizationId?: string;
    currentSummarizer?: {id: string; name: string};
    preserveUnavailableValue?: boolean;
    loadOptions?: boolean;
    value?: string;
    onChange?: (value: string | undefined) => void;
}

export default function AiReportSummarizerSelect({
    organizationId,
    currentSummarizer,
    preserveUnavailableValue = true,
    loadOptions = true,
    value,
    onChange
}: Props) {
    const appContext = useContext(AppContextContext);
    const service = useContext(AiReportSummarizerServiceContext);
    const [summarizers, setSummarizers] = useState<AiReportSummarizer[]>();
    const [loading, setLoading] = useState(false);
    const canRead = appContext.user.hasOneOfPermission(aiReportSummarizerReadPermissions);

    useEffect(() => {
        if (!canRead || !loadOptions) {
            return;
        }

        let cancelled = false;

        setLoading(true);

        service.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => {
                if (!cancelled) {
                    setSummarizers(result.data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSummarizers(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [service, canRead, loadOptions]);

    const availableSummarizers = (summarizers ?? []).filter(summarizer => summarizer.owner === ResourceOwnerType.SYSTEM
        || (organizationId && summarizer.owner === ResourceOwnerType.ORGANIZATION && summarizer.organization?.id === organizationId));
    const systemOptions = availableSummarizers.filter(summarizer => summarizer.owner === ResourceOwnerType.SYSTEM)
        .map(summarizer => ({value: summarizer.id, label: summarizer.name}));
    const organizationSummarizers = availableSummarizers.filter(summarizer => summarizer.owner === ResourceOwnerType.ORGANIZATION);
    const organizationOptions = organizationSummarizers.map(summarizer => ({value: summarizer.id, label: summarizer.name}));
    const options = orderSystemAndOrganizationOptionGroups(
        systemOptions.length ? [{label: "System", options: systemOptions}] : [],
        organizationOptions.length ? [{label: organizationSummarizers[0].organization.name, options: organizationOptions}] : [],
        appContext.user.isSystemAdmin()
    );
    const selectedAvailable = availableSummarizers.some(summarizer => summarizer.id === value);
    const selectedUnavailable = !!currentSummarizer && currentSummarizer.id === value && !selectedAvailable;

    useEffect(() => {
        if (!preserveUnavailableValue && value && (!canRead || (summarizers && !selectedAvailable))) {
            onChange?.(undefined);
            message.info("The original AI report summarizer is unavailable. The global default will be used.");
        }
    }, [preserveUnavailableValue, canRead, summarizers, value, selectedAvailable, onChange]);

    return <Select
        value={value}
        onChange={onChange}
        loading={loading}
        disabled={!canRead}
        allowClear={true}
        showSearch={true}
        optionFilterProp="label"
        placeholder="Global default"
        options={selectedUnavailable ? [
            {value: currentSummarizer.id, label: currentSummarizer.name, disabled: true},
            ...options
        ] : options}
    />;
}
